<template>
  <div class="container app-shell" :class="{ 'studio-route': isStudioRoute, 'detail-route': isDetailRoute, 'gacha-route': isGachaRoute, 'membership-route': isMembershipRoute }">
    <TopNavigation
      v-if="!isStudioRoute"
      :current-user="currentUser"
      :is-logged-in="isLoggedIn"
      @create-message-login="openLoginModal"
      @logout="openLogoutConfirm"
      @notice="showNoticeStatus"
    />

    <router-view />

    <SiteFooter v-if="!isStudioRoute && !isMembershipRoute" />

    <HelpFeedback />

    <LoginModal
      :open="loginModalOpen"
      :challenge="loginChallenge"
      :auth-status="state.authStatus"
      :email-status="emailAuthStatus"
      :email-busy="emailAuthBusy"
      :email-code-sent-nonce="emailCodeSentNonce"
      @close="closeLoginModal"
      @create-message-login="createMessageLogin"
      @refresh-status="refreshMessageLoginStatus"
      @send-email-code="sendEmailLoginCode"
      @email-login="loginWithEmail"
    />

    <ToastHost />

    <ConfirmDialog
      :open="logoutConfirmOpen"
      eyebrow="账号操作"
      title="确定要退出拼豆物语吗？"
      message="退出后云端进度不会丢失；下次签到、抽卡或生成图纸时，需要重新登录。"
      confirm-text="确认退出"
      cancel-text="继续创作"
      danger
      @cancel="logoutConfirmOpen = false"
      @confirm="logoutAndClose"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import TopNavigation from "./components/TopNavigation.vue";
import SiteFooter from "./components/SiteFooter.vue";
import LoginModal from "./components/LoginModal.vue";
import ConfirmDialog from "./components/ConfirmDialog.vue";
import ToastHost from "./components/ToastHost.vue";
import HelpFeedback from "./components/HelpFeedback.vue";
import { useStudio } from "./composables/useStudio";
import { useToast } from "./composables/useToast";
import { useAnalytics } from "./composables/useAnalytics";

const {
  state,
  currentUser,
  loginChallenge,
  emailAuthBusy,
  emailAuthStatus,
  emailCodeSentNonce,
  isLoggedIn,
  initialize,
  createMessageLogin,
  refreshMessageLoginStatus,
  sendEmailLoginCode,
  loginWithEmail,
  stopLoginFlow,
  logout
} = useStudio();

const loginModalOpen = ref(false);
const { toast } = useToast();
const logoutConfirmOpen = ref(false);
const route = useRoute();
const { track, rememberSource, rememberAttribution } = useAnalytics();
const isStudioRoute = computed(() => route.name === "studio");
const isDetailRoute = computed(() => route.name === "work-detail");
const isGachaRoute = computed(() => route.name === "gacha");
const isMembershipRoute = computed(() => route.name === "membership");

onMounted(() => {
  initialize();
  window.addEventListener("bead-open-login", openLoginModal);
});

onBeforeUnmount(() => {
  window.removeEventListener("bead-open-login", openLoginModal);
});

watch(
  () => isLoggedIn.value,
  (loggedIn) => {
    if (loggedIn) {
      loginModalOpen.value = false;
    }
  }
);

watch(
  () => route.fullPath,
  (path) => {
    const attribution = rememberAttribution(route.query);
    const source = rememberSource(route.query.source || route.query.utm_source || attribution.source);
    track("PAGE_VIEW", { path, source, attribution });
  },
  { immediate: true }
);

function openLoginModal() {
  if (isLoggedIn.value) {
    loginModalOpen.value = false;
    return;
  }
  loginModalOpen.value = true;
}

function closeLoginModal() {
  loginModalOpen.value = false;
  stopLoginFlow();
}

function openLogoutConfirm() {
  logoutConfirmOpen.value = true;
}

function showNoticeStatus() {
  toast.info("消息中心正在建设中，重要的登录、发布和会员结果会先通过页面提示通知你。", { title: "消息中心" });
}

async function logoutAndClose() {
  loginModalOpen.value = false;
  logoutConfirmOpen.value = false;
  await logout();
}

</script>

<style scoped>
.app-shell{
  padding-top:var(--nav-height);
  padding-bottom:10px;
}
.app-shell.studio-route{
  width:100%;
  max-width:none;
  margin:0;
  padding-top:0;
  padding-bottom:0;
}
.app-shell.gacha-route{
  width:100%;
  max-width:none;
  margin:0;
}
.gacha-route :deep(.site-footer){display:none}
.membership-route :deep(.site-footer){display:none}
@media(max-width:680px){
  .app-shell{padding-bottom:84px}
  .app-shell.detail-route{padding-top:0;padding-bottom:78px}
  .detail-route :deep(.site-header),.detail-route :deep(.mobile-nav),.detail-route :deep(.site-footer){display:none}
  .gacha-route{padding-top:0}
  .gacha-route :deep(.site-header),.gacha-route :deep(.site-footer){display:none}
}
@media(max-width:760px){
  .app-shell.gacha-route{width:100%!important;max-width:100%!important;margin:0!important;overflow-x:hidden}
  .gacha-route :deep(.mobile-nav){left:0;right:0;width:auto;max-width:none}
  .app-shell.membership-route{width:100%!important;max-width:100%!important;margin:0!important;padding-top:0;overflow-x:hidden}
  .membership-route :deep(.site-header),.membership-route :deep(.mobile-nav){display:none}
}
</style>

